import mixpanel from 'mixpanel-browser';

let initialized = false;

const DEBUG =
  process.env.NODE_ENV !== 'production' ||
  process.env.REACT_APP_ANALYTICS_DEBUG === 'true';

function log(...args: unknown[]): void {
  if (DEBUG) console.log('[analytics]', ...args);
}

export function initAnalytics(token: string | undefined): void {
  if (initialized) {
    log('init skipped — already initialized');
    return;
  }
  if (!token) {
    console.warn('[analytics] Mixpanel token missing — events will be dropped.');
    return;
  }
  mixpanel.init(token, {
    track_pageview: false,
    persistence: 'localStorage',
    debug: DEBUG,
    loaded: () => log('Mixpanel loaded, distinct_id:', mixpanel.get_distinct_id()),
  });
  initialized = true;
  log('init OK, token:', `${token.slice(0, 6)}…`);
  if (DEBUG) (window as unknown as { mixpanel: typeof mixpanel }).mixpanel = mixpanel;
}

/**
 * Fire a Mixpanel event. Resolves once Mixpanel confirms delivery,
 * or after `timeoutMs` so a flaky network never blocks navigation.
 */
export function trackEvent(
  event: string,
  properties?: Record<string, unknown>,
  timeoutMs = 400
): Promise<void> {
  return new Promise((resolve) => {
    if (!initialized) {
      log(`drop "${event}" — not initialized`, properties);
      resolve();
      return;
    }

    log(`→ track "${event}"`, properties);
    const startedAt = performance.now();

    let settled = false;
    const finish = (reason: 'sent' | 'timeout' | 'error') => {
      if (settled) return;
      settled = true;
      const ms = Math.round(performance.now() - startedAt);
      log(`← "${event}" ${reason} in ${ms}ms`);
      resolve();
    };

    try {
      mixpanel.track(event, properties, { send_immediately: true }, () =>
        finish('sent')
      );
    } catch (err) {
      log(`error tracking "${event}":`, err);
      finish('error');
      return;
    }
    setTimeout(() => finish('timeout'), timeoutMs);
  });
}
